import { returnColDefsForLegs } from "@/components/widgets/tradeActionSummary/helper/actionSummaryMainTableColDefs";
import {
  applyColumnWidthMultiplier,
  defaultColDef,
  getColumnWidthMultiplier,
  getFontSizePx,
  getHeaderHeight,
  getRowHeight,
} from "@/components/widgets/tradeActionSummary/helper/actionSummaryHelper";
import useTableUIStore from "@/store/tableUIStore";
import { ExecutionStrategy } from "@/lib/interfaces and Enums/InterfaceExecutionStrategy";
import { ExecutionStrategyStatus } from "@/lib/interfaces and Enums/ENUMExecutionStrategyStatus";
import { useTheme } from "next-themes";
import { AgGridReact } from "ag-grid-react";
import { useEffect, useMemo, useRef } from "react";
import tradeBlockDataStore from "@/store/tradeBlockDataStore";
import tradeSettingsStore from "@/store/tradeSettingsStore";
import { useCellSelectionManager } from "../../helper/useCellSelectionManager";

interface ILegsTable {
  data: ExecutionStrategy;
  getAccessTokenSilently: Function;
}

const LegsTable = ({ data, getAccessTokenSilently }: ILegsTable) => {
  const { theme } = useTheme();
  const gridRef = useRef<AgGridReact>(null);
  const fontSize = useTableUIStore((state) => state.fontSize);
  const rowHeightSetting = useTableUIStore((state) => state.rowHeight);
  const dynamicRowHeight = useMemo(
    () => getRowHeight(rowHeightSetting),
    [rowHeightSetting],
  );
  const dynamicHeaderHeight = useMemo(
    () => getHeaderHeight(rowHeightSetting),
    [rowHeightSetting],
  );
  const columnWidthMultiplier = useMemo(
    () => getColumnWidthMultiplier(fontSize),
    [fontSize],
  );

  // Subscribe to settings store to reactively update columns when trade type changes
  const tradeSettingsData = tradeSettingsStore(
    (state) => state.tradeSettingsData,
  );

  // Subscribe to block store reactively so columns refresh when blocks arrive
  const tradeBlockData = tradeBlockDataStore((state) => state.tradeBlockData);

  // Get the active trade type for this trade (only relevant for New status)
  const activeTradeType = useMemo(() => {
    if (data.status !== ExecutionStrategyStatus.New) return null;
    const thisTradeSettings = tradeSettingsData.find(
      (t) => t.esid === data.esid,
    )?.settings;
    return thisTradeSettings?.tradeTypes?.find((t) => t.active)?.name;
  }, [data.esid, data.status, tradeSettingsData]);

  const colDefsObject = {
    theme,
    expandedRowsContextCurrent: [],
    setExpandedRowsContext: () => {},
    fullTradeData: data,
    getAccessTokenSilently,
    setExpandedRowsContextMostRecentlyUpdated: () => {},
    isShowingInSelectPanel: true,
  };

  const columnDefs = useMemo(() => {
    const cols = returnColDefsForLegs(colDefsObject);
    return applyColumnWidthMultiplier(cols, columnWidthMultiplier);
  }, [data, theme, activeTradeType, tradeBlockData, columnWidthMultiplier]);

  // Update grid columns when trade type or font size changes (AG Grid doesn't remove columns automatically)
  useEffect(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.setGridOption("columnDefs", columnDefs);
    }
  }, [columnDefs]);

  // Scale column widths at runtime when font size changes
  useEffect(() => {
    if (gridRef.current?.api) {
      const allColumns = gridRef.current.api.getColumns();
      if (allColumns) {
        allColumns.forEach((column) => {
          const colDef = column.getColDef() as any;
          if (colDef.fixedWidth) return;
          if (colDef.width) {
            const baseWidth = colDef.width;
            const newWidth = Math.round(baseWidth * columnWidthMultiplier);
            gridRef.current?.api.setColumnWidth(column, newWidth);
          }
          if (colDef.minWidth) {
            const baseMinWidth = colDef.minWidth;
            const newMinWidth = Math.round(
              baseMinWidth * columnWidthMultiplier,
            );
            column.getColDef().minWidth = newMinWidth;
          }
        });
      }
    }
  }, [fontSize, columnWidthMultiplier]);

  // const rowData = useMemo(() => data.legs || [], [data.legs]);

  const rowData =
    tradeBlockData.find((trade) => trade.esid == data.esid)?.blocks || [];
  // // if the zustand store for the blocks does not have the correct data, return empty array.
  // if (tradeDetails.isCancelledOPWithNoCP && dataToPass?.length == 0) {
  //   getBlocksData({
  //     esid: params.data.esid,
  //     getAccessTokenSilently: getAccessTokenSilently,
  //   });
  // }

  // Manage cell selection behavior across all grids
  const { onCellMouseDown } = useCellSelectionManager();

  // Show right click menu outside of table width and height
  const popupParent = useMemo<HTMLElement | null>(() => {
    return document.querySelector(".GridContainer");
  }, []);

  return (
    <div
      data-testid="legsTableContainer"
      style={{
        maxHeight: "200px",
        overflow: "auto",
        fontSize: `${getFontSizePx(fontSize)}px`,
      }}
      className={`${theme === "dark" ? "ag-theme-balham-dark" : "ag-theme-balham"} w-full`}
    >
      <AgGridReact
        ref={gridRef}
        columnDefs={columnDefs}
        rowData={rowData}
        defaultColDef={defaultColDef}
        headerHeight={dynamicHeaderHeight}
        suppressRowClickSelection={true}
        // suppressCellFocus={false}
        enableRangeSelection={true}
        // Method of selecting rows
        onCellMouseDown={onCellMouseDown}
        stopEditingWhenCellsLoseFocus={true}
        rowSelection={"multiple"}
        suppressAggFuncInHeader={true}
        suppressDragLeaveHidesColumns={true}
        // Style:
        rowHeight={dynamicRowHeight}
        singleClickEdit={true}
        scrollbarWidth={6}
        tooltipShowDelay={1}
        domLayout="autoHeight"
        popupParent={popupParent}
      />
    </div>
  );
};

export default LegsTable;
